/**
 * Bridge helpers between the Item Finder form state and the AI assistant: serializing the
 * current searches into chat context, and validating/targeting agent-driven search updates.
 */
import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { SearchTypeEnum } from '@/itemFinder/itemFinder.constants';
import { SCOPED_TO_GLOBALLY_SCOPED_NULL_VALUE, findScopedToProperty } from '@/itemFinder/itemFinder.utilities';
import { OperatorEnum } from '@seeqdev/api/model/PropertyFilterInputV1';
import type { FixedListSearch, Property, Search } from '@/itemFinder/itemFinder.constants';

export interface AgentContextSearchValue {
  value: string | number | boolean;
  unitOfMeasure?: string;
}

export interface AgentContextSearchPredicate {
  propertyName: string;
  operator: string;
  values: AgentContextSearchValue[];
}

export interface AgentContextSearch {
  index: number;
  searchId: string;
  configured: boolean;
  searchType?: string;
  isInclude?: boolean;
  types?: string[];
  ancestorId?: string;
  folderId?: string;
  predicates?: AgentContextSearchPredicate[];
  itemIds?: string[];
  /**
   * Read-only state of the hidden Scoped To predicate: whether this property search is restricted
   * to items scoped to the current workbook, rather than also matching globally scoped ones.
   * Absent when the search carries no Scoped To predicate at all. The form spells the same setting
   * as its "Limit search results to those scoped to this Analysis" checkbox.
   */
  limitedToCurrentWorkbook?: boolean;
}

export interface AIUpdatePayload {
  searchIndex: number;
  searchId?: string;
  propertySearch?: Record<string, unknown>;
  fixedListSearch?: Record<string, unknown>;
}

const unwrapId = (id: unknown): string | undefined =>
  typeof id === 'object' && id !== null ? (id as { id?: string }).id : (id as string | undefined);

const omitUndefined = <T extends Record<string, unknown>>(obj: T): T =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;

/** Treats null the same as undefined/absent: the backend may serialize None fields as null. */
const isPresent = (value: unknown): boolean => value !== undefined && value !== null;

/**
 * Every member of the `Search` union types `searchType` as the whole enum, so it cannot
 * discriminate the union on its own; pairing it with a presence check on the fixed list's own
 * `itemIds` field narrows to the one member that actually carries the item ids.
 */
const isFixedListSearch = (search: Search): search is FixedListSearch =>
  search.searchType === SearchTypeEnum.FIXEDLIST && 'itemIds' in search;

/**
 * Whether a hidden Scoped To predicate restricts its search to items scoped to the workbook.
 *
 * `isScopedToLimitedToCurrentWorkbook` answers the same question for the form's own toggle, but it
 * also requires the predicate's single value to equal a caller-supplied workbook id — the toggle
 * has to read as off when the predicate names some *other* workbook. That id isn't available here
 * (and for a table-scoped form the relevant id is the table's own scopedTo, not the open workbook),
 * so the shape alone is used: the two forms `buildScopedToPropertyValues` ever writes are EQUALS
 * over the single scoping id (restricted) and WITHIN over the globally-scoped null sentinel
 * alongside it (not restricted, because globally scoped items match too). Which workbook it is
 * stays out of the context deliberately — the agent needs the on/off state, not the id.
 */
const isScopedToLimitedToWorkbook = (scopedTo: Property): boolean =>
  scopedTo.operator === OperatorEnum.EQUALS &&
  (scopedTo.values ?? []).length === 1 &&
  scopedTo.values[0]?.value !== SCOPED_TO_GLOBALLY_SCOPED_NULL_VALUE;

export function serializeSearchesForAgentContext(searches: Search[]): AgentContextSearch[] {
  const serialized = (searches ?? []).map((search, arrayIndex): AgentContextSearch => {
    const base = { index: arrayIndex + 1, searchId: String(search.searchId ?? arrayIndex) };
    if (!search.searchType) {
      return { ...base, configured: false };
    }
    if (search.searchType === SearchTypeEnum.PROPERTY) {
      const scopedTo = findScopedToProperty(search.predicates);
      const predicates = (search.predicates ?? [])
        // Half-configured predicates (property chosen, operator not yet picked — the normal
        // intermediate form state) and the hidden Scoped To predicate (re-derived from
        // ensureSearchScopedTo, not the model's concern) are both excluded.
        .filter(
          (predicate) =>
            predicate.propertyName && predicate.operator && predicate.propertyName !== SeeqNames.Properties.ScopedTo,
        )
        .map((predicate) =>
          omitUndefined({
            propertyName: predicate.propertyName,
            operator: predicate.operator as string,
            values: (predicate.values ?? []).map((value) =>
              omitUndefined({
                value: value.value,
                unitOfMeasure: (value as { unitOfMeasure?: string }).unitOfMeasure,
              }),
            ),
          }),
        );
      return omitUndefined({
        ...base,
        configured: true,
        searchType: search.searchType as string,
        isInclude: search.isInclude ?? true,
        types: search.types as string[] | undefined,
        ancestorId: unwrapId(search.ancestorId),
        folderId: unwrapId(search.folderId),
        predicates,
        // TODO CRAB-63225: expose this as a mutable field on the update tool.
        limitedToCurrentWorkbook: scopedTo ? isScopedToLimitedToWorkbook(scopedTo) : undefined,
      });
    }
    if (isFixedListSearch(search)) {
      return omitUndefined({
        ...base,
        configured: true,
        searchType: search.searchType,
        isInclude: search.isInclude ?? true,
        itemIds: search.itemIds,
      });
    }
    // Other search types (hierarchy, stored table, scaling, asset-tree variants) are surfaced
    // by type only: the agent can see they exist but cannot modify them.
    // TODO CRAB-63224: serialize and support modifying non-property search types.
    return omitUndefined({
      ...base,
      configured: true,
      searchType: search.searchType as string,
      isInclude: search.isInclude ?? true,
    });
  });
  return serialized.some((search) => search.configured) ? serialized : [];
}

/**
 * Resolves the search a model-driven update targets. A present searchId (explicit null counts
 * as absent, matching the backend's None-as-null serialization) is authoritative: if it doesn't
 * match anything, the target is refused (`undefined`) rather than falling back to the index —
 * an index that happens to line up with a stale/wrong searchId must not clobber the wrong search.
 * The index fallback only applies when no searchId was supplied at all.
 */
export function resolveAgentSearchTarget(
  searches: Search[],
  target: { searchIndex: number; searchId?: string | null },
): number | undefined {
  if (isPresent(target.searchId)) {
    const byId = searches.findIndex((s) => s.searchId === target.searchId);
    return byId !== -1 ? byId : undefined;
  }
  const byIndex = target.searchIndex - 1;
  return Number.isInteger(byIndex) && byIndex >= 0 && byIndex < searches.length ? byIndex : undefined;
}

const AGENT_REPLACEABLE_SEARCH_TYPES: string[] = ['', SearchTypeEnum.PROPERTY, SearchTypeEnum.FIXEDLIST];

export function isAgentReplaceableSearch(search: Search | undefined): boolean {
  return search !== undefined && AGENT_REPLACEABLE_SEARCH_TYPES.includes((search.searchType as string) ?? '');
}

// Mirrors the shape checks in itemFinder.utilities.ts's isPropertySearch/isFixedListSearch, which
// are module-private there. Kept here rather than exporting those, since this module only
// consumes Search/SearchTypeEnum per its produces contract.
const isPropertyFilterValueLike = (value: unknown): boolean =>
  value !== null && typeof value === 'object' && 'value' in (value as Record<string, unknown>);

const isPropertyPredicateLike = (predicate: unknown): boolean => {
  if (!predicate || typeof predicate !== 'object') {
    return false;
  }
  const candidate = predicate as Record<string, unknown>;
  if (!('operator' in candidate) || !('propertyName' in candidate) || !('values' in candidate)) {
    return false;
  }
  return Array.isArray(candidate.values) && candidate.values.every(isPropertyFilterValueLike);
};

const isPropertySearchPayload = (search: unknown): boolean => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  const candidate = search as Record<string, unknown>;
  if (candidate.searchType !== SearchTypeEnum.PROPERTY) {
    return false;
  }
  if (!('isInclude' in candidate) || !('types' in candidate)) {
    return false;
  }
  if ('predicates' in candidate) {
    if (!Array.isArray(candidate.predicates)) {
      return false;
    }
    if (!candidate.predicates.every(isPropertyPredicateLike)) {
      return false;
    }
  }
  return true;
};

const isFixedListSearchPayload = (search: unknown): boolean => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  const candidate = search as Record<string, unknown>;
  return (
    candidate.searchType === SearchTypeEnum.FIXEDLIST &&
    typeof candidate.isInclude === 'boolean' &&
    Array.isArray(candidate.itemIds)
  );
};

export function isValidAIUpdatePayload(
  data: Record<string, unknown>,
): data is Record<string, unknown> & AIUpdatePayload {
  const { searchIndex, searchId, propertySearch, fixedListSearch } = data as Partial<AIUpdatePayload>;
  if (typeof searchIndex !== 'number' || !Number.isInteger(searchIndex) || searchIndex < 1) {
    return false;
  }
  if (isPresent(searchId) && typeof searchId !== 'string') {
    return false;
  }
  const hasProperty = isPresent(propertySearch);
  const hasFixedList = isPresent(fixedListSearch);
  if (hasProperty === hasFixedList) {
    return false; // exactly one of the two must be present
  }
  return hasProperty ? isPropertySearchPayload(propertySearch) : isFixedListSearchPayload(fixedListSearch);
}
